import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { renderPriceDecimalColumn } from 'components/lists/functions/renderListColumn';
import InvoiceTemplateModal from './InvoiceTemplateModal';
import { renderScheduleStatusBadge } from './InvoiceScheduleCard';
import { renderScheduleSentence } from './InvoiceTemplateList';

// Renders the contract a periodic invoice belongs to as a link
const renderScheduleContract = (row: any) => {
    const schedule = row.schedule;
    if (!schedule?.contract) return <span>-</span>;
    const label = [schedule.contract_number, schedule.contract_reference].filter(Boolean).join(' - ') || String(schedule.contract);
    return (
        <Link to={`/contracts/${schedule.contract}`} onClick={(event) => event.stopPropagation()}>
            {label}
        </Link>
    );
};

// Shows the periodic invoices of a contact above its normal invoices
const InvoiceTemplateDetailList: React.FC<{
    itemId: string;
}> = ({
    itemId,
}) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { userLocale } = useSettings();
    const [hasTemplates, setHasTemplates] = useState(false);

    // Determine whether the contact has periodic invoices to show the list for
    const fetchHasTemplates = useCallback(async () => {
        const response = await fetchData({
            apiUrl: 'get_invoice_template_list',
            params: { recipient: itemId, deleted: false, page_size: 1 },
            handleLogout
        });
        setHasTemplates((response.count || 0) > 0);
    }, [itemId]);

    // Check for periodic invoices when opening the tab
    useEffect(() => {
        fetchHasTemplates();
    }, [fetchHasTemplates]);

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        {
            width: 'minmax(0, 1fr)',
            field: 'number',
            label: 'invoice.general.invoice_number_label',
            render: (_, row) => <span>{row.number ? t('invoice.template.number_label', { number: row.number }) : '-'}</span>
        },
        {
            width: 'minmax(0, 2fr)',
            field: 'internal_reference',
            label: 'invoice.general.internal_reference_label'
        },
        {
            width: 'minmax(0, 3fr)',
            field: 'schedule',
            label: 'invoice.schedule.card_label',
            disableOrdering: true,
            render: (_, row) => renderScheduleSentence(row, t, userLocale)
        },
        {
            width: 'minmax(0, 1fr)',
            field: 'amount',
            label: 'invoice.general.amount_label',
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale)
        },
        {
            width: 'minmax(0, 1.5fr)',
            field: 'contract',
            label: 'contract.general.service_contract_label',
            disableOrdering: true,
            render: (_, row) => renderScheduleContract(row)
        },
        {
            width: '144px',
            field: 'status',
            label: 'invoice.general.status_label',
            disableOrdering: true,
            render: (_, row) => renderScheduleStatusBadge(!!row.schedule?.is_active, t)
        }
    ];

    // Without periodic invoices the list is left out
    if (!hasTemplates) return null;

    return (
        <div className='detail-list invoice-template-detail-list'>
            <h5 className='invoice-template-detail-list-title'>{t('invoice.template.object_name.plural')}</h5>
            <AbstractList
                query={{
                    endpoint: 'get_invoice_template_list',
                    defaultParams: { recipient: itemId, deleted: false },
                    object: 'invoice_template'
                }}
                objectName='invoice.template.object_name'
                columns={columns}
                showSearch={false}
                showAddButton={false}
                buttonSize='small'
                isPaginated={false}
                detailModal={<InvoiceTemplateModal />}
                onRowClick='detailModal'
            />
        </div>
    );
};

export default InvoiceTemplateDetailList;
